import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Colors,
    FOCUS_RING_NO_RADIUS,
    ModalDialog,
    MultilineInput,
    Text,
} from '@amelio/ui-library';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProficiencyLevelPresentation } from './useProficiencyLevelPresentation';

import type { ProficiencyLevel, SkillItem } from '@/types/performance-profile';
import type { ColorType } from '@amelio/ui-library';
import type { FormEvent } from 'react';

import { ProficiencyIcon } from '@/components/ProficiencyIcon/ProficiencyIcon';
import { getIconEntry } from '@/constants/proficiencyIconCatalog';

export interface SkillProficiencyModalRef {
    open: (skill: SkillItem) => void;
    close: () => void;
}

interface SkillProficiencyModalProps {
    onSave: (
        skillId: string,
        proficiency: ProficiencyLevel,
        comment: string,
        cycleInstanceId: string,
    ) => void;
    isSaving?: boolean;
    dataTestId?: string;
}

const NA_LABEL_KEY = 'performanceProfile.skillsetAssessment.proficiencyLevels.na';

export const SkillProficiencyModal = forwardRef<
    SkillProficiencyModalRef,
    SkillProficiencyModalProps
>(({ onSave, isSaving = false, dataTestId = 'skill-proficiency-modal' }, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [skill, setSkill] = useState<SkillItem | null>(null);
    const [selectedProficiencyId, setSelectedProficiencyId] = useState<string>('');
    const [comment, setComment] = useState('');

    const {
        displayLevels: selectableLevels,
        proficiencyLevelMap,
        getProficiencyLabel: getLabel,
        getProficiencyIcon,
        isLoading: isLoadingLevels,
        isError: hasLevelsError,
    } = useProficiencyLevelPresentation();

    // Three reasons the grid can be empty, and they are not interchangeable. Only the third is
    // an observed absence; the other two mean we could not ask yet. Saying "no proficiency level
    // found" while a request is in flight or has failed states something we never checked, and
    // leaves the user writing a comment for an assessment that cannot be saved (#15036).
    const hasNoLevels = !selectableLevels || selectableLevels.length === 0;
    const canSelectLevel = !isLoadingLevels && !hasLevelsError && !hasNoLevels;

    const close = () => setIsOpen(false);

    useImperativeHandle(ref, () => ({
        open: (nextSkill: SkillItem) => {
            setSkill(nextSkill);
            setSelectedProficiencyId(!nextSkill.proficiency ? '' : nextSkill.proficiency.id);
            setComment('');
            setIsOpen(true);
        },
        close,
    }));

    const handleSave = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // canSelectLevel is checked here as well as on the button: opening a skill that already
        // has a proficiency pre-selects it, so a failed load would otherwise leave a selection
        // in state with no levels to resolve it against, and save a null proficiency. The same goes
        // for isSaving: Enter submits the form even while the button is disabled.
        if (!skill || !selectedProficiencyId || !canSelectLevel || isSaving) {
            return;
        }

        // Find the full proficiency object from the selectable levels
        const selectedLevel = selectableLevels?.find((level) => level === selectedProficiencyId);
        const proficiencyObject = selectedLevel
            ? {
                  id: selectedLevel,
                  label: { en: getLabel(selectedLevel, t(NA_LABEL_KEY)) },
                  icon: getProficiencyIcon(selectedLevel)?.name || '',
                  sortOrder: 0,
              }
            : null;

        // Call the original onSave which handles the API call
        onSave(skill.id, proficiencyObject, comment.trim(), skill.cycleInstanceId);
    };

    const handleProficiencyClick = (level: string) => {
        setSelectedProficiencyId(level);
    };

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={close}
            title={skill?.name ?? ''}
            subtitle={t('performanceProfile.skillsetAssessment.proficiencyModal.subtitle')}
            size={'md'}
            closeAriaLabel={t('common.close')}
            backdropBlur
            dataTestId={dataTestId}
            onSubmit={handleSave}
            footer={
                <div className={'flex justify-end gap-3'}>
                    <ButtonSecondary
                        size={'md'}
                        onClick={close}
                        disabled={isSaving}
                        dataTestId={`${dataTestId}-cancel-button`}
                    >
                        {t('common.cancel')}
                    </ButtonSecondary>

                    <ButtonGradientPrimary
                        type={'submit'}
                        size={'md'}
                        disabled={!canSelectLevel || !selectedProficiencyId || isSaving}
                        dataTestId={`${dataTestId}-save-button`}
                    >
                        {t('common.save')}
                    </ButtonGradientPrimary>
                </div>
            }
        >
            <div className={'flex flex-col gap-6'}>
                <div className={'flex flex-col gap-4'}>
                    <Text
                        weight={'semibold'}
                        size={'default'}
                        color={Colors.NAVY}
                        className={'text-body-sm!'}
                    >
                        {t(
                            'performanceProfile.skillsetAssessment.proficiencyModal.proficiencyLevel',
                        )}
                    </Text>

                    <div
                        className={
                            'grid grid-cols-2 gap-3 min-[420px]:!grid-cols-3 sm:!grid-cols-4'
                        }
                    >
                        {!canSelectLevel ? (
                            <Text
                                size={'small'}
                                color={Colors.BASE_400}
                                data-testid={`${dataTestId}-levels-message`}
                            >
                                {isLoadingLevels
                                    ? t(
                                          'performanceProfile.skillsetAssessment.proficiencyModal.proficiencyLevelsLoading',
                                      )
                                    : hasLevelsError
                                      ? t(
                                            'performanceProfile.skillsetAssessment.proficiencyModal.proficiencyLevelsUnavailable',
                                        )
                                      : t(
                                            'performanceProfile.skillsetAssessment.proficiencyModal.noProficiencyLevelFound',
                                        )}
                            </Text>
                        ) : (
                            selectableLevels.map((level) => {
                                const profData = proficiencyLevelMap[level];
                                const iconUrl = profData?.iconUrl;
                                const iconName = profData?.icon;
                                const iconColor = profData?.color || Colors.BASE_300;
                                const isSelected = selectedProficiencyId === level;

                                const iconEntry = iconName ? getIconEntry(iconName) : undefined;

                                return (
                                    <button
                                        key={level}
                                        type={'button'}
                                        onClick={() => handleProficiencyClick(level)}
                                        data-testid={`${dataTestId}-level-${level}`}
                                        aria-pressed={isSelected}
                                        className={`${FOCUS_RING_NO_RADIUS} flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 p-4 transition-colors hover:shadow-md`}
                                        style={{
                                            backgroundColor: isSelected ? iconColor : Colors.WHITE,
                                            borderColor: iconColor,
                                        }}
                                    >
                                        <ProficiencyIcon
                                            iconUrl={iconUrl}
                                            iconEntry={iconEntry}
                                            alt={getLabel(level, t(NA_LABEL_KEY))}
                                            color={isSelected ? Colors.WHITE : iconColor}
                                        />
                                        <Text
                                            size={'small'}
                                            weight={'semibold'}
                                            color={
                                                isSelected ? Colors.WHITE : (iconColor as ColorType)
                                            }
                                            align={'center'}
                                            className={'text-body-sm!'}
                                        >
                                            {getLabel(level, t(NA_LABEL_KEY))}
                                        </Text>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>

                <div className={'flex flex-col gap-2'}>
                    <Text
                        weight={'semibold'}
                        size={'small'}
                        color={Colors.NAVY}
                        className={'text-body-sm!'}
                    >
                        {t('performanceProfile.skillsetAssessment.proficiencyModal.commentLabel')}
                    </Text>
                    <MultilineInput
                        value={comment}
                        onChange={setComment}
                        rows={3}
                        placeholder={t(
                            'performanceProfile.skillsetAssessment.proficiencyModal.commentPlaceholder',
                        )}
                        dataTestId={`${dataTestId}-comment-input`}
                    />
                </div>
            </div>
        </ModalDialog>
    );
});

SkillProficiencyModal.displayName = 'SkillProficiencyModal';
